import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type {
  BriefResponseDto,
  CommentResponseDto,
} from '@inference.engine/workspace-api-client/web';
import { PROJECT_COMMENT_QUERY_KEYS } from '@/entities/project-comment';
import { notificationService } from '@/lib/services/notification.service';
import { createAgentComment } from '../api/agent-comments.api';
import { runAgentThreadResponse } from '../api/brief-review.api';
import { serializeBriefForReview } from '../lib/serialize-brief';
import { serializeThread, resolveRespondTargets } from '../lib/serialize-thread';
import { textToTiptap } from '../lib/text-to-tiptap';
import { useTranslation } from 'react-i18next';

/**
 * After a comment is created, decides whether an agent should answer
 * (`resolveRespondTargets`) and, if so, runs it statelessly (brief + full
 * serialized thread in the prompt) and posts the reply into the same thread,
 * attributed to the agent. Fire-and-forget: errors surface as notifications.
 */
export function useAgentThreadResponder() {
  const { t } = useTranslation('chats');

  const queryClient = useQueryClient();

  const maybeRespond = useCallback(
    async (params: {
      brief: BriefResponseDto;
      platformProjectId: string;
      newComment: CommentResponseDto;
      root: CommentResponseDto;
      replies: CommentResponseDto[];
      selection?: { selectedText: string; sectionId: string };
    }) => {
      const targets = resolveRespondTargets({ newComment: params.newComment, root: params.root });
      if (targets.length === 0) return;

      // Audience matching is irrelevant here — only the serialized text is used.
      const input = serializeBriefForReview(params.brief, []);
      const briefText = [
        `# ${input.title}`,
        ...input.sections.map((s) => `## ${s.name} (id: ${s.sectionId})\n${s.contentText}`),
      ].join('\n');
      const threadText = serializeThread(params.root, params.replies);
      const projectId = params.platformProjectId;

      for (const agentId of targets) {
        try {
          const { text, contextId } = await runAgentThreadResponse({
            agentId,
            briefText,
            threadText,
            selection: params.selection,
          });
          if (!text.trim()) continue;
          await createAgentComment(
            projectId,
            { targetType: 'brief', targetId: params.brief.id },
            {
              content: textToTiptap(text),
              parentId: params.root.id,
              authorType: 'agent',
              agentId,
              contextId: contextId ?? undefined,
            },
          );
        } catch (error) {
          notificationService.showError(
            t('briefAgentReview.useAgentThreadResponder.agentReplyFailed'),
            {
              description: error instanceof Error ? error.message : undefined,
            },
          );
        }
      }
      void queryClient.invalidateQueries({
        queryKey: PROJECT_COMMENT_QUERY_KEYS.list(projectId, 'brief', params.brief.id),
      });
    },
    [queryClient, t],
  );

  return { maybeRespond };
}
